// Phone numbers are stored in ONE canonical shape everywhere: E.164, e.g.
// "+6281234567890". Before 2026-08 they were bare Indonesian local digits
// ("81234567890") with "+62" bolted on at the edges that needed it (Firebase
// OTP, wa.me links), which made a non-Indonesian number unstorable.
//
// Mirrors spott-platform's src/shared/utils/phone.ts and spott-client's
// src/shared/phone.ts. KEEP ALL THREE FILES IDENTICAL. user.phone_number is a
// globally @unique column matched by exact string, so a divergence here does
// not throw: it silently splits one person into two accounts.
//
// The metadata bundle is "mobile", not "min" or "max", and that is deliberate.
// "min" validates LENGTH ONLY, so it accepts +62843243423 (no such Indonesian
// prefix) and +62215551234 (a Jakarta landline). The old /^8\d{7,11}$/ rule
// did guarantee an Indonesian mobile, and "mobile" metadata preserves that
// guarantee for every country at +4KB gzip over "min".
import {
  parsePhoneNumberFromString,
  isValidPhoneNumber,
  getCountries,
  getCountryCallingCode,
  AsYouType,
} from "libphonenumber-js/mobile";
import type { CountryCode } from "libphonenumber-js/mobile";

/** Indonesia stays the default everywhere a country is not chosen explicitly. */
export const DEFAULT_COUNTRY: CountryCode = "ID";

/**
 * Canonicalize any user-typed or legacy-shaped number to E.164.
 *
 * Returns null only when the input cannot be read as a phone number at all.
 * A parseable but INVALID number (wrong prefix, landline, too short) still
 * comes back canonicalized, so lookups can match legacy rows. Callers that
 * write must gate on isValidPhone first.
 *
 * libphonenumber handles the shapes we care about natively when given a
 * default country: "81234567890", "081234567890" (trunk prefix) and
 * "6281234567890" (bare calling code) all resolve to "+6281234567890".
 */
export const toE164 = (
  raw: string | null | undefined,
  defaultCountry: CountryCode = DEFAULT_COUNTRY,
): string | null => {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return null;
  const parsed = parsePhoneNumberFromString(trimmed, defaultCountry);
  return parsed ? parsed.number : null;
};

/**
 * toE164, falling back to the trimmed input when it cannot be parsed.
 *
 * For READ and lookup paths: an unreadable phone keeps today's behavior
 * (matches nothing, 404) instead of becoming a new failure. Never use this on
 * a write.
 */
export const toE164OrRaw = (
  raw: string | null | undefined,
  defaultCountry: CountryCode = DEFAULT_COUNTRY,
): string => toE164(raw, defaultCountry) ?? (raw ?? "").trim();

/**
 * Is this a real, dialable MOBILE number?
 *
 * Mobile-only because every phone we hold is a contact channel: customers and
 * instructors receive their login code on it, admins receive an SMS OTP, and
 * the studio's own number becomes a wa.me link. A landline satisfies none of
 * those. This is the generic form of the guarantee /^8\d{7,11}$/ used to give
 * for Indonesia alone.
 */
export const isValidPhone = (
  raw: string | null | undefined,
  defaultCountry: CountryCode = DEFAULT_COUNTRY,
): boolean => {
  const e164 = toE164(raw, defaultCountry);
  return e164 ? isValidPhoneNumber(e164) : false;
};

/** Display form, always carrying the country code: "+62 812 3456 7890". */
export const formatPhone = (raw: string | null | undefined): string => {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return "";
  const parsed = parsePhoneNumberFromString(trimmed, DEFAULT_COUNTRY);
  return parsed ? parsed.formatInternational() : trimmed;
};

/** E.164 digits with no "+", for wa.me links and any digits-only consumer. */
export const toWhatsAppDigits = (raw: string | null | undefined): string | null => {
  const e164 = toE164(raw);
  return e164 ? e164.slice(1) : null;
};

/** True when the number belongs to Indonesia. Unparseable numbers are not. */
export const isIndonesian = (raw: string | null | undefined): boolean => {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return false;
  const parsed = parsePhoneNumberFromString(trimmed, DEFAULT_COUNTRY);
  return parsed?.country === "ID";
};

// A calling code can belong to many countries (+1 covers 25, +7 covers 2).
// Only the flag shown while parsing a value back into the picker depends on
// this, never the stored number, so picking a sensible primary is enough.
const PRIMARY_COUNTRY_FOR_CODE: Record<string, CountryCode> = {
  "1": "US",
  "7": "RU",
};

let callingCodeToCountry: Record<string, CountryCode> | null = null;

/** ISO country for a calling code ("62" -> "ID"). Null when unknown. */
export const countryForCallingCode = (callingCode: string): CountryCode | null => {
  if (!callingCodeToCountry) {
    callingCodeToCountry = {};
    for (const country of getCountries()) {
      const code = getCountryCallingCode(country);
      if (!callingCodeToCountry[code]) callingCodeToCountry[code] = country;
    }
    for (const code of Object.keys(PRIMARY_COUNTRY_FOR_CODE)) {
      callingCodeToCountry[code] = PRIMARY_COUNTRY_FOR_CODE[code];
    }
  }
  return callingCodeToCountry[callingCode] ?? null;
};

/**
 * Split a stored E.164 into picker state.
 *
 * Also handles the INCOMPLETE drafts a phone input emits mid-typing ("+62812"),
 * which libphonenumber refuses to parse, by matching the longest calling code.
 */
export const splitE164 = (
  value: string | null | undefined,
): { country: CountryCode; nationalNumber: string } => {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return { country: DEFAULT_COUNTRY, nationalNumber: "" };

  const parsed = parsePhoneNumberFromString(trimmed, DEFAULT_COUNTRY);
  if (parsed?.country) {
    return { country: parsed.country, nationalNumber: parsed.nationalNumber };
  }

  if (trimmed.startsWith("+")) {
    const digits = trimmed.slice(1).replace(/\D/g, "");
    // Calling codes are 1-3 digits; try longest first so "+1" never shadows "+123".
    for (let len = 3; len >= 1; len--) {
      const country = countryForCallingCode(digits.slice(0, len));
      if (country) return { country, nationalNumber: digits.slice(len) };
    }
  }

  return { country: DEFAULT_COUNTRY, nationalNumber: trimmed.replace(/\D/g, "") };
};

/**
 * Compose picker state into E.164.
 *
 * Returns "" — not "+62" — when no digits have been typed, so an AntD
 * `required` rule still sees an empty field rather than a non-empty string.
 * Leading zeros are dropped because they are a trunk prefix (ID, GB, DE, AU),
 * never part of the international form.
 */
export const joinE164 = (country: CountryCode, nationalNumber: string): string => {
  const digits = (nationalNumber || "").replace(/\D/g, "").replace(/^0+/, "");
  if (!digits) return "";
  return `+${getCountryCallingCode(country)}${digits}`;
};

/**
 * The calling code of a stored number ("+6591234567" -> "65"), without the
 * plus. Empty string when the value cannot be read.
 */
export const callingCodeOf = (raw: string | null | undefined): string => {
  const e164 = toE164(raw);
  if (!e164) return "";
  const { nationalNumber } = splitE164(e164);
  return e164.slice(1, e164.length - nationalNumber.length);
};

/** Live grouping while a national number is being typed. */
export const formatAsYouType = (nationalNumber: string, country: CountryCode): string =>
  new AsYouType(country).input(nationalNumber);

/**
 * The national number after an edit to a formatAsYouType-rendered input.
 * AsYouType re-inserts every separator it knows, so deleting one (the ")" in
 * "(415) 555-2671", the "-" in "0812-3456-7890") leaves the digits unchanged
 * and the character reappears on the next render: Backspace does nothing and
 * the digits before it are unreachable. When an edit removed only formatting,
 * drop the digit that preceded it instead, which is what the user meant.
 * Every other edit (typing, deleting a digit, a selection replace) passes
 * through as typed.
 */
export const nationalAfterEdit = (previousFormatted: string, nextRaw: string): string => {
  const previousDigits = previousFormatted.replace(/\D/g, "");
  const nextDigits = nextRaw.replace(/\D/g, "");
  if (nextDigits !== previousDigits || nextRaw.length >= previousFormatted.length) {
    return nextRaw;
  }
  let divergence = 0;
  while (
    divergence < nextRaw.length &&
    nextRaw[divergence] === previousFormatted[divergence]
  ) {
    divergence += 1;
  }
  const digitsBefore = previousFormatted.slice(0, divergence).replace(/\D/g, "").length;
  if (digitsBefore === 0) return nextRaw;
  return previousDigits.slice(0, digitsBefore - 1) + previousDigits.slice(digitsBefore);
};

/** Emoji flag from an ISO country code. Windows renders these as letter pairs. */
const flagFor = (country: string): string =>
  country.replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

export interface CountryDialOption {
  country: CountryCode;
  callingCode: string;
  name: string;
  flag: string;
}

/**
 * Options for a country picker: Indonesia first, then localized alphabetical.
 * Country names come from Intl.DisplayNames, so no name table ships.
 */
export const countryDialOptions = (locale = "id"): CountryDialOption[] => {
  let displayNames: { of: (code: string) => string | undefined } | null = null;
  try {
    displayNames = new Intl.DisplayNames([locale], { type: "region" });
  } catch {
    displayNames = null;
  }

  const options = getCountries().map((country) => ({
    country,
    callingCode: getCountryCallingCode(country),
    name: displayNames?.of(country) ?? country,
    flag: flagFor(country),
  }));

  const collator = new Intl.Collator(locale);
  options.sort((a, b) => {
    if (a.country === DEFAULT_COUNTRY) return -1;
    if (b.country === DEFAULT_COUNTRY) return 1;
    return collator.compare(a.name, b.name);
  });
  return options;
};
